// 2026-08-31 - FormuLearning - 붙여넣기 grid 어댑터 (이슈 #58 S5 4차/#68, M84-72)
// 파일 위치: frontend/src/lib/pasteGrid.ts
// 목적: 엑셀에서 drag&copy 한 **범위**를 화면 입력표에 그대로 붙여넣는다.
//       정본 = UI/UX v2.2 §5.9 `COMP-PASTE-GRID`, 선정 근거 = `Q-260813-22`(260831 승인).
//
// **자체 `onPaste` 어댑터다 — 의존성이 늘지 않는다.** 우리 격자는 형태가 고정돼 있고 작다
// (용출표 = 시점 7행 × 용액 3~4열). 범위 선택·채우기 핸들·undo 같은 스프레드시트 편집
// UX 가 요구되지 않으므로 `clipboardData.getData('text/plain')` 을 분해해 채우는 것으로 족하다.
// AG Grid Community 는 범위 붙여넣기가 Enterprise 기능이고 Handsontable 은 상용 유료라 기각,
// react-datasheet-grid(MIT) 는 차순위로 보류했다(갭분석 §5.3).
//
// 이 파일은 **컴포넌트를 모른다.** 파싱·정렬·좌표 계산만 하고, 어디에 쓸지는 `write` 콜백이 정한다.
//
// 규약 3가지 — 화면 안내문과 같아야 한다:
//   ① **빈 셀은 건너뛴다**(기존 값 보존). 엑셀은 빈 셀 붙여넣기를 "지우기"로 처리하지만 우리
//      격자는 기본값이 0(용출)이라 조용히 덮으면 실측이 사라진다. 지우기는 칸을 직접 비운다.
//   ② **숫자가 아닌 셀은 건너뛴다**(세어서 알린다). 머리글이 섞여 들어와도 값이 밀리지 않는다.
//   ③ **단일 셀 붙여넣기는 가로채지 않는다**(`isRangePaste()` 판정). 평범한 한 칸 붙여넣기의
//      커서·선택 동작을 바꿀 이유가 없다.

/** 붙여넣기 1회의 결과 — 화면 고지문의 소스다. */
export interface PasteResult {
  /** 실제로 채운 칸 수 */
  filled: number
  /** 숫자가 아니어서 건너뛴 칸 수(빈 칸은 세지 않는다) */
  skipped: number
  /** 표 아래로 넘쳐 버린 행 수 */
  overflowRows: number
  /** 표 오른쪽으로 넘쳐 버린 열 수 */
  overflowCols: number
}

const EMPTY_RESULT: PasteResult = { filled: 0, skipped: 0, overflowRows: 0, overflowCols: 0 }

/**
 * 범위 붙여넣기인가 — 탭이나 개행이 있으면 범위다.
 * 단일 셀이면 `false` 를 돌려 호출부가 `preventDefault` 하지 않게 한다(규약 ③).
 */
export function isRangePaste(text: string): boolean {
  return text.includes('\t') || text.includes('\n') || text.includes('\r')
}

/**
 * 클립보드 TSV → 행×열 문자열 행렬.
 *
 * 엑셀은 탭·개행·따옴표를 품은 셀을 `"` 로 감싸고 내부 `"` 를 `""` 로 이스케이프한다.
 * 따옴표를 인식하지 않으면 그런 셀 하나가 여러 칸으로 찢어지므로 따옴표 상태를 들고 훑는다.
 */
export function parseClipboardMatrix(text: string): string[][] {
  if (text === '') return []

  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]

    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"'
          i += 1
        } else {
          quoted = false
        }
      } else {
        cell += ch
      }
      continue
    }

    if (ch === '"' && cell === '') {
      quoted = true
    } else if (ch === '\t') {
      row.push(cell)
      cell = ''
    } else if (ch === '\n' || ch === '\r') {
      // CRLF 는 한 번의 줄바꿈으로 센다.
      if (ch === '\r' && text[i + 1] === '\n') i += 1
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
    } else {
      cell += ch
    }
  }

  row.push(cell)
  rows.push(row)

  // 후행 개행이 만든 빈 행만 떨어낸다(중간의 빈 행은 사용자가 복사한 그대로 둔다).
  while (rows.length > 0) {
    const last = rows[rows.length - 1]
    if (last.length === 1 && last[0].trim() === '') rows.pop()
    else break
  }
  return rows
}

/** 천단위 콤마 표기인가 — `1,234` · `12,345.6` 만 참. `54,0`(유럽식 소수)은 거짓이다. */
const THOUSANDS = /^-?\d{1,3}(,\d{3})+(\.\d+)?$/

/**
 * 셀 문자열 → 숫자. 빈 칸·해석 불가는 `null` 이다.
 *
 * `%` 접미와 공백은 떼어낸다(엑셀 셀 서식이 그대로 딸려 오는 일이 흔하다).
 * 콤마는 **천단위 표기일 때만** 지운다 — `54,0` 을 540 으로 만들면 소리 없이 10배가 된다.
 */
export function parseCellNumber(raw: string): number | null {
  let s = raw.trim().replace(/\s/g, '')
  if (s === '') return null
  if (s.endsWith('%')) s = s.slice(0, -1)
  if (THOUSANDS.test(s)) s = s.replace(/,/g, '')
  if (s === '' || s.includes(',')) return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

/** 그 행에 숫자로 읽히는 칸이 하나라도 있는가. */
const hasNumber = (row: string[]) => row.some((c) => parseCellNumber(c) !== null)

/**
 * 선두의 **머리글 행**을 떨어낸다 — 숫자 칸이 하나도 없는 행이 대상이다.
 * 엑셀에서 표를 통째로 긁어 오면 `min | AVG | SD` 같은 행이 함께 온다.
 * 데이터 행에는 숫자가 있으므로 걸리지 않는다.
 */
export function stripHeaderRows(matrix: string[][]): string[][] {
  let start = 0
  while (start < matrix.length && !hasNumber(matrix[start])) start += 1
  return matrix.slice(start)
}

/**
 * **색인 열 동반 복사 보정** — 첫 열이 표의 색인(용출표의 `min` 시점)과 **전부 정확히 일치**할
 * 때만 그 열을 버린다.
 *
 * 시점 열은 읽기 전용인데(격자 정본 = Lot 대조약, BR-LOT-09·10) 사용자는 표를 통째로 복사한다.
 * 보정하지 않으면 열이 하나씩 밀려 AVG 자리에 시점이 들어간다.
 * 일치할 때만 버리므로 **오검출이 없다** — 값이 다르면 손대지 않고 초과 열로 고지된다.
 *
 * @param expected 표의 색인 값들(앵커 행부터 순서대로)
 */
export function dropIndexColumn(matrix: string[][], expected: number[]): string[][] {
  if (matrix.length === 0 || matrix[0].length < 2) return matrix
  const n = Math.min(matrix.length, expected.length)
  if (n === 0) return matrix
  for (let r = 0; r < n; r += 1) {
    if (parseCellNumber(matrix[r][0] ?? '') !== expected[r]) return matrix
  }
  return matrix.map((row) => row.slice(1))
}

export interface ApplyMatrixArgs {
  matrix: string[][]
  /** 붙여넣기가 시작될 행(표에 **보이는** 행 서수) */
  anchorRow: number
  /** 붙여넣기가 시작될 열 */
  anchorCol: number
  /** 표의 행 수(보이는 행만) */
  rowCount: number
  /** 표의 열 수 */
  colCount: number
  /** 한 칸 쓰기 — 호출부가 자기 draft 좌표로 옮긴다 */
  write: (row: number, col: number, value: number) => void
}

/**
 * 2차원 채우기 — 앵커 칸부터 **행×열 그대로** 반영한다(정본 `COMP-PASTE-GRID` 행동).
 * 표를 넘는 분은 버리고 몇 행·몇 열이 버려졌는지 센다.
 */
export function applyMatrix({
  matrix, anchorRow, anchorCol, rowCount, colCount, write,
}: ApplyMatrixArgs): PasteResult {
  if (matrix.length === 0) return { ...EMPTY_RESULT }

  const usableRows = Math.max(0, rowCount - anchorRow)
  const usableCols = Math.max(0, colCount - anchorCol)
  const widest = matrix.reduce((m, row) => Math.max(m, row.length), 0)

  const res: PasteResult = {
    filled: 0,
    skipped: 0,
    overflowRows: Math.max(0, matrix.length - usableRows),
    overflowCols: Math.max(0, widest - usableCols),
  }

  for (let r = 0; r < Math.min(matrix.length, usableRows); r += 1) {
    const row = matrix[r]
    for (let c = 0; c < Math.min(row.length, usableCols); c += 1) {
      const raw = row[c]
      if (raw.trim() === '') continue // 규약 ① — 빈 칸은 기존 값을 남긴다
      const v = parseCellNumber(raw)
      if (v === null) {
        res.skipped += 1 // 규약 ②
        continue
      }
      write(anchorRow + r, anchorCol + c, v)
      res.filled += 1
    }
  }
  return res
}

export interface ApplyLinearArgs {
  matrix: string[][]
  /** 붙여넣기가 시작될 입력 인덱스 */
  anchorIndex: number
  /** 입력 칸 총수 */
  count: number
  write: (index: number, value: number) => void
}

/**
 * 1차원 채우기 — 행렬을 **행 우선으로 평탄화**해 앵커부터 순서대로 채운다.
 *
 * 결과 입력 패널은 값 N개가 한 줄로 늘어선 형상이라 엑셀에서 **가로 1×N 으로 복사하든
 * 세로 N×1 으로 복사하든 같은 뜻**이다. 2차원 그대로 반영하면 세로 복사가 첫 칸만 들어간다.
 */
export function applyLinear({ matrix, anchorIndex, count, write }: ApplyLinearArgs): PasteResult {
  const flat = matrix.flat()
  if (flat.length === 0) return { ...EMPTY_RESULT }

  const usable = Math.max(0, count - anchorIndex)
  const res: PasteResult = {
    filled: 0,
    skipped: 0,
    // 1차원이라 넘친 분은 "칸"으로 세되 고지문을 위해 행 쪽에 담는다.
    overflowRows: Math.max(0, flat.length - usable),
    overflowCols: 0,
  }

  for (let i = 0; i < Math.min(flat.length, usable); i += 1) {
    const raw = flat[i]
    if (raw.trim() === '') continue
    const v = parseCellNumber(raw)
    if (v === null) {
      res.skipped += 1
      continue
    }
    write(anchorIndex + i, v)
    res.filled += 1
  }
  return res
}

/**
 * 결과 → 화면 고지 한 줄. 정본이 *"초과분은 버리고 **그 사실을 알린다**"* 라고 규정한다.
 * 아무것도 안 채워지고 알릴 것도 없으면 `null`(고지를 띄우지 않는다).
 *
 * @param overflowUnit 넘친 분의 단위 — 2차원은 `'행'`, 1차원은 `'칸'`
 */
export function describePaste(res: PasteResult, overflowUnit: '행' | '칸' = '행'): string | null {
  const parts: string[] = []
  if (res.filled > 0) parts.push(`${res.filled}칸 채움`)
  if (res.overflowRows > 0) parts.push(`${res.overflowRows}${overflowUnit} 버림(표 범위 초과)`)
  if (res.overflowCols > 0) parts.push(`${res.overflowCols}열 버림(표 범위 초과)`)
  if (res.skipped > 0) parts.push(`${res.skipped}칸 건너뜀(숫자 아님)`)
  if (parts.length === 0) return null
  return `붙여넣기: ${parts.join(' · ')}`
}
